<template>
  <BackPageLayout :back-page="{
    name: 'Profile'
  }" color="white" topbar-color="white" class="min-h-screen">
    <template #headline>
      {{ component.title }}
    </template>

    <div class="text-sm">

      <div class="">
        <div class="mb-2 w-full flex flex-between flex-1">
          <div class="w-2/3">
            <h2 class="font-bold">
              {{ $t('editcomponent.type.label') }}
            </h2>

            <Transition name="grow-and-fade" mode="out-in">
              <p v-if="!showType" class="text-gray-500 ">
                {{ $t(`editcomponent.type.current`, {
                  type:
                    $t(`newcomponent.type.${component.type}`)
                }) }}
              </p>
            </Transition>
          </div>

          <div class="grow text-right">
            <ModifyButton :active="showType"
              @click="setActiveRef('showType')" />
          </div>
        </div>

        <Transition name="grow-and-fade" mode="out-in">
          <div v-show="showType">
            <div>
              <ComponentTypeForm />

              <SaveButton :loading="sending" @click="update" class="mt-2">
                {{ $t('Save') }}
              </SaveButton>
            </div>
          </div>
        </Transition>
      </div>

      <Divider class="my-4" />

      <div class="">
        <div class="w-full flex flex-between flex-1">
          <div class="w-2/3">
            <h2 class="font-bold">
              {{ $t('editcomponent.date.label') }}
            </h2>

            <Transition name="grow-and-fade" mode="out-in">
              <p v-if="!showDate" class="text-gray-500 ">
                {{ $t(`editcomponent.date.current`, {
                  span: component.periodBeforeDate,
                  unit: labels[component.type]
                }) }}
              </p>
            </Transition>
          </div>
          <div class="grow text-right">
            <ModifyButton :active="showDate"
              @click="setActiveRef('showDate')" />
          </div>
        </div>

        <Transition name="grow-and-fade" mode="out-in">
          <div v-show="showDate">
            <ComponentDateForm class="mt-2" />
            <SaveButton :loading="sending" @click="update" class="mt-2">
              {{ $t('Save') }}
            </SaveButton>
          </div>
        </Transition>
      </div>

      <Divider class="my-4" />

      <div class="">
        <div class="mb-2 w-full flex flex-between flex-1">
          <div class="w-2/3">
            <h2 class="font-bold">
              {{ $t('editcomponent.dataset.label') }}
            </h2>
            <Transition name="grow-and-fade" mode="out-in">
              <div v-if="!showDataset">
                <p class="text-gray-500 ">
                  {{ $t(`newcomponent.datasets.origin.${component.dataOrigin}`)
                  }}
                </p>
                <p class="text-gray-500 ">
                  {{ $t(`newcomponent.datasets.type.${component.dataType}`) }}
                </p>
              </div>
            </Transition>
          </div>

          <div class="grow text-right">
            <ModifyButton :active="showDataset"
              @click="setActiveRef('showDataset')" />
          </div>
        </div>

        <Transition name="grow-and-fade" mode="out-in">
          <div v-show="showDataset">
            <ComponentDatasetForm class="mt-2" />
            <SaveButton :loading="sending" @click="update" class="mt-2">
              {{ $t('Save') }}
            </SaveButton>
          </div>
        </Transition>
      </div>


      <Divider class="my-4" />

      <div class="pb-4">
        <div class="mb-2 w-full flex flex-between flex-1">
          <div class="w-2/3">
            <h2 class="font-bold">
              {{ $t('editcomponent.chart.label') }}
            </h2>

            <Transition name="grow-and-fade" mode="out-in">

              <p v-if="!showChart" class="text-gray-500 ">
                {{ $t(`editcomponent.date.current`, {
                  span: component.periodBeforeDate,
                  unit: labels[component.type]
                }) }}
              </p>
            </Transition>
          </div>
          <div class="grow text-right">
            <ModifyButton :active="showChart"
              @click="setActiveRef('showChart')" />
          </div>
        </div>

        <Transition name="grow-and-fade" mode="out-in">
          <div v-if="showChart">
            <ComponentChartForm class="mt-2" :edit="true" />
            <SaveButton @click="update"
              :status="sending ? 'pressed' : 'default'" class="mt-2">
              {{ $t('Save') }}
            </SaveButton>
          </div>
        </Transition>
      </div>

      <div class="w-full flex justify-end">
        <TextButton @click="confirmDeleteIsOpen = true" color="red">{{
          $t('editcomponent.delete')
        }}</TextButton>
      </div>

      <FormDialog :isOpen="confirmDeleteIsOpen" :title="$t('editcomponent.delete')"
        :sending="sendingDeletion" :confirmText="$t('Delete')" :closeText="$t('Cancel')"
        color="danger" @close="closeConfirmDelete" @submit-form="confirmDelete">

        {{ $t('editcomponent.confirm_delete') }}
      </FormDialog>

    </div>
  </BackPageLayout>
</template>
<script setup lang="ts">
import { useNewComponentStore } from '../../stores/newComponentStore';
import { useSalesReportsStore } from '../../stores/salesReports';
import { useUserManagerStore } from '../../stores/userManagerStore';
import { computed, ref } from 'vue';
import { onBeforeRouteLeave, useRouter } from 'vue-router';
import ComponentTypeForm from '../../components/form/ComponentTypeForm.vue'
import ComponentDateForm from '../../components/form/ComponentDateForm.vue'
import ComponentDatasetForm from '../../components/form/ComponentDatasetForm.vue'
import ComponentChartForm from '../../components/form/ComponentChartForm.vue'
import BackPageLayout from '../../layouts/BackPageLayout.vue';
import Divider from '../../components/Divider.vue';
import ModifyButton from '../../components/buttons/ModifyButton.vue'
import FormDialog from '../../components/dialogs/FormDialog.vue'
import TextButton from '../../components/buttons/TextButton.vue'
import { useI18n } from 'vue-i18n';
import SaveButton from '../../components/buttons/SaveButton.vue';
import { useSnackbarStore } from '../../stores/snackbarStore';
import { useAppSettingsStore } from '../../stores/AppSettingsStore';

const { t } = useI18n();

const router = useRouter()
const reportId = router.currentRoute.value.params.id as string;

const newComponentStore = useNewComponentStore();
const salesReportsStore = useSalesReportsStore();
const userManagerStore = useUserManagerStore();
const snackbarStore = useSnackbarStore();
const appSettingsStore = useAppSettingsStore();
const component = computed(() => {
  return newComponentStore.newComponent;
});

const showType = ref(false);
const showDate = ref(false);
const showDataset = ref(false);
const showChart = ref(false);

const refs = { showType, showDate, showDataset, showChart };

const confirmDeleteIsOpen = ref(false);
const sendingDeletion = ref(false);
const closeConfirmDelete = () => {
  confirmDeleteIsOpen.value = false;
}
const confirmDelete = async () => {
  await appSettingsStore.startLoadingPage(3);
  sendingDeletion.value = true;
  appSettingsStore.advance()
  const success = await salesReportsStore.deleteReport(reportId);
  appSettingsStore.advance()
  sendingDeletion.value = false;

  if (success) {
    confirmDeleteIsOpen.value = false;
    snackbarStore.addSnackbar(t('editcomponent.snackbar.deleted'), 'danger');
    await appSettingsStore.finishLoadingPage();
    router.push({ name: 'Profile' });
  }
}

function setActiveRef(activeRefName: string) {
  const key = activeRefName as keyof typeof refs;
  const isActiveRefAlreadyTrue = refs[key].value;

  // Set all refs to false initially
  for (const refValue of Object.values(refs)) {
    refValue.value = false;
  }

  // If the active ref was already true, it gets toggled to false (already done above).
  // If it was not true, set it to true.
  if (!isActiveRefAlreadyTrue) {
    refs[key].value = true;
  }
}

const closeAll = () => {
  showType.value = false;
  showDate.value = false;
  showDataset.value = false;
  showChart.value = false;
}

const labels = computed(() => {
  return {
    daily: t('newcomponent.dates.day', newComponentStore.newComponent.periodBeforeDate),
    weekly: t('newcomponent.dates.week', newComponentStore.newComponent.periodBeforeDate),
    monthly: t('newcomponent.dates.month', newComponentStore.newComponent.periodBeforeDate),
    yearly: t('newcomponent.dates.year', newComponentStore.newComponent.periodBeforeDate),
    custom: ''
  }
})

const sending = ref(false);

const update = async () => {
  sending.value = true;
  await newComponentStore.send();
  sending.value = false;
  salesReportsStore.addToRefreshList(reportId);
  closeAll();
  snackbarStore.addSnackbar(t('editcomponent.snackbar.updated'), 'default');
  // reload the data now for the preview to have the new data

  await salesReportsStore.refreshCentersComponents();
}

onBeforeRouteLeave(async () => {
  if (salesReportsStore.reportsToFetch.includes(reportId)) {
    await userManagerStore.getUserInfo();
    salesReportsStore.refreshCentersComponents();
  }
})
</script>

<style scoped>
.grow-and-fade-enter-active {
  animation: grow-and-fade 0.5s ease-in-out;
  overflow: hidden;
  /* Empêche le contenu de déborder pendant l'animation */
}

.grow-and-fade-leave-active {
  animation: grow-and-fade 0.5s reverse ease-in-out;
  overflow: hidden;
  /* Empêche le contenu de déborder pendant l'animation */
}

.grow-and-fade-enter-from,
.grow-and-fade-leave-to {
  opacity: 0;
  max-height: 0;
}

.grow-and-fade-enter-to,
.grow-and-fade-leave-from {
  opacity: 1;
  max-height: 500px;
  /* Ajustez cette valeur en fonction de la hauteur maximale attendue de l'élément */
}

@keyframes grow-and-fade {
  0% {
    opacity: 0;
    max-height: 0;
  }



  50% {
    max-height: 500px;
    opacity: 0;
  }

  100% {
    opacity: 1;
    max-height: 500px;
  }
}
</style>